import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  searchFoods,
  createCustomFood,
  updateCustomFood,
  deleteCustomFood,
} from "../../firebase";

/**
 * Food catalog (מאגר המזונות) — nutrition clinics only.
 *
 * Search results are cached per (query, filters, cursor) so the food picker can
 * reopen instantly, but the catalog itself is never mirrored wholesale into
 * Redux — it grows without bound and only one page is ever on screen.
 */
export const nutritionFoodsApi = createApi({
  reducerPath: "nutritionFoodsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Foods"],
  endpoints: (builder) => ({
    // Global catalog + this clinic's custom foods, merged and paginated
    searchFoods: builder.query({
      async queryFn({ clinicId, searchQuery = "", filters = {}, cursor = null, pageSize = 25 }) {
        try {
          const result = await searchFoods(clinicId, searchQuery, filters, {
            cursor,
            pageSize,
          });
          return { data: result };
        } catch (error) {
          console.error("[CK-7808] Error searching foods:", {
            errorCode: "CK-7808",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "Foods", id: "LIST" }],
    }),

    // Add a clinic food (also raises a NiV promotion request)
    createCustomFood: builder.mutation({
      async queryFn({ clinicId, foodData, therapistId, therapistName }) {
        try {
          const food = await createCustomFood(clinicId, foodData, {
            therapistId,
            therapistName,
          });
          return { data: food };
        } catch (error) {
          console.error("[CK-7809] Error creating custom food:", {
            errorCode: "CK-7809",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Foods", id: "LIST" }],
    }),

    updateCustomFood: builder.mutation({
      async queryFn({ clinicId, foodId, foodData, therapistId, therapistName }) {
        try {
          const food = await updateCustomFood(clinicId, foodId, foodData, {
            therapistId,
            therapistName,
          });
          return { data: food };
        } catch (error) {
          console.error("[CK-7810] Error updating custom food:", {
            errorCode: "CK-7810",
            errorMessage: error.message,
            clinicId,
            foodId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Foods", id: "LIST" }],
    }),

    deleteCustomFood: builder.mutation({
      async queryFn({ clinicId, foodId, foodName, therapistId, therapistName }) {
        try {
          const result = await deleteCustomFood(clinicId, foodId, {
            therapistId,
            therapistName,
            foodName,
          });
          return { data: result };
        } catch (error) {
          console.error("[CK-7811] Error deleting custom food:", {
            errorCode: "CK-7811",
            errorMessage: error.message,
            clinicId,
            foodId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Foods", id: "LIST" }],
    }),
  }),
});

export const {
  useSearchFoodsQuery,
  useLazySearchFoodsQuery,
  useCreateCustomFoodMutation,
  useUpdateCustomFoodMutation,
  useDeleteCustomFoodMutation,
} = nutritionFoodsApi;
